import type {
  MessageContent,
  ThreadMessage,
  ToolCallContent,
} from '@/types/messages';
import { callArtifact, readArtifact } from '@/helpers/artifact.helpers';
import type {
  Clarification,
  ClarificationQuestion,
} from '@/types/artifacts.types';
import { ASK_USER_SKIPPED_RESULT, type AskUserReply } from '@/types/tools';

/**
 * Extracts the stable source ID from an assistant-ui message.
 *
 * The @assistant-ui/react library assigns its own internal IDs to messages
 * that do NOT match the UUIDs in our runtime context. We embed the original
 * message UUID in `metadata.custom._sourceId` (see AssistantUIAdapter) so we
 * can always resolve the correct enhanced message regardless of what ID the
 * library assigned.
 */
export function extractMessageSourceId(message: {
  metadata: unknown;
  id: string;
}): string {
  const custom = (
    message.metadata as { custom?: { _sourceId?: string } } | undefined
  )?.custom;
  return custom?._sourceId ?? message.id;
}

/** The clarification card a part draws; null for any other part. */
function clarificationOf(part: MessageContent): Clarification | null {
  if (part.type !== 'tool-call') return null;
  const artifact = callArtifact(part as ToolCallContent);
  return artifact?.kind === 'clarification' &&
    artifact.outcome.state !== 'rejected'
    ? artifact
    : null;
}

/**
 * The questions of a clarification card: an `ask_user` call whose args the
 * reasoner took. Null for any other content part.
 */
export function getClarificationQuestions(
  part: MessageContent,
): ClarificationQuestion[] | null {
  return clarificationOf(part)?.questions ?? null;
}

/**
 * Whether `part` is a clarification card still waiting for the user: its
 * ask_user call runs and has no result yet.
 */
export function isPendingAskUser(
  part: MessageContent,
): part is ToolCallContent {
  if (clarificationOf(part)?.outcome.state !== 'waiting') return false;
  const toolCall = part as ToolCallContent;
  return (
    (toolCall.status === 'pending' || toolCall.status === 'executing') &&
    toolCall.result == null &&
    !toolCall.isError
  );
}

/** The card a streaming turn of `messages` waits on, if any. */
export function findPendingAskUser(
  messages: readonly ThreadMessage[],
): { threadId: string; toolCallId: string } | null {
  for (const message of messages) {
    if (message.role !== 'assistant' || message.status !== 'streaming') {
      continue;
    }
    const part = message.content.find(isPendingAskUser);
    if (part) {
      return { threadId: message.threadId, toolCallId: part.toolCallId };
    }
  }
  return null;
}

/** The tool result the reasoner makes of `reply`. */
export function askUserResult(reply: AskUserReply): string {
  return reply.kind === 'skip' ? ASK_USER_SKIPPED_RESULT : reply.text;
}

/** The outcome the reasoner gives a clarification card for `reply`. */
function askUserOutcome(reply: AskUserReply): Clarification['outcome'] {
  return reply.kind === 'skip'
    ? { state: 'skipped', text: null }
    : { state: 'answered', text: reply.text };
}

/** The ask_user block `call` closed by `reply`, as its TOOL_RESULT closes it. */
function repliedCall(
  call: ToolCallContent,
  reply: AskUserReply,
): ToolCallContent {
  const closed: ToolCallContent = {
    ...call,
    result: askUserResult(reply),
    isError: false,
    status: 'complete',
  };
  const artifact = readArtifact(call.artifact);
  if (artifact?.kind !== 'clarification') return closed;
  const answered: Clarification = {
    ...artifact,
    outcome: askUserOutcome(reply),
  };
  return { ...closed, artifact: answered };
}

/**
 * `messages` with the ask_user call `toolCallId` of `threadId` closed by
 * `reply`: its result, and on a clarification card its outcome, as the
 * reasoner will close it.
 */
export function withAskUserReply(
  messages: ThreadMessage[],
  threadId: string,
  toolCallId: string,
  reply: AskUserReply,
): ThreadMessage[] {
  const isTarget = (part: MessageContent) =>
    part.type === 'tool-call' &&
    (part as ToolCallContent).toolCallId === toolCallId;
  return messages.map((message) =>
    message.threadId === threadId && message.content.some(isTarget)
      ? {
          ...message,
          content: message.content.map((part) =>
            isTarget(part)
              ? repliedCall(part as ToolCallContent, reply)
              : part,
          ),
        }
      : message,
  );
}

/**
 * Whether `message` closed a clarification card unanswered (skipped or
 * expired): its turn ends without answering the prompt.
 */
export function hasUnansweredAskUser(
  message: Pick<ThreadMessage, 'content'>,
): boolean {
  return message.content.some((part) => {
    const state = clarificationOf(part)?.outcome.state;
    return state === 'skipped' || state === 'expired';
  });
}
